/**
 * SlowmoFXManager — Drives the SlowmoFXPipeline based on ImpactTimeSystem state.
 *
 * Each frame, reads ImpactTimeSystem.intensity and updates the pipeline uniform.
 * Lazily attaches/detaches the pipeline to the main camera to avoid GPU cost when idle.
 *
 * Usage (from GameScene):
 *   create():  SlowmoFXManager.init(this);
 *   update():  SlowmoFXManager.update();
 *   shutdown(): SlowmoFXManager.shutdown();
 */

import Phaser from 'phaser';
import { ImpactTimeSystem } from '@/systems/ImpactTimeSystem';
import { SlowmoFXPipeline } from '@/systems/SlowmoFXPipeline';

class SlowmoFXManagerClass {
  private scene: Phaser.Scene | null = null;
  private pipeline: SlowmoFXPipeline | null = null;
  private attached = false;

  /** Call once from GameScene.create() */
  init(scene: Phaser.Scene): void {
    this.scene = scene;
    this.attached = false;
    this.pipeline = null;

    // Register pipeline if not already registered
    SlowmoFXPipeline.register(scene);
  }

  /** Call every frame from GameScene.update() */
  update(): void {
    if (!this.scene) return;

    const intensity = ImpactTimeSystem.intensity;

    if (intensity > 0.001) {
      // Attach pipeline on first need
      if (!this.attached) {
        const cam = this.scene.cameras.main;
        if (cam) {
          cam.setPostPipeline(SlowmoFXPipeline);
          this.attached = true;
        }
      }
      // Update the pipeline's intensity uniform
      if (this.attached) {
        const cam = this.scene.cameras.main;
        const pipe = cam?.getPostPipeline(SlowmoFXPipeline);
        if (pipe) {
          // getPostPipeline can return array or single instance
          const p = Array.isArray(pipe) ? pipe[0] : pipe;
          if (p instanceof SlowmoFXPipeline) {
            p.intensity = intensity;
            this.pipeline = p;
          }
        }
      }
    } else if (this.attached) {
      // Remove pipeline when effect ends to save GPU
      const cam = this.scene.cameras.main;
      if (cam) {
        cam.removePostPipeline('SlowmoFXPipeline');
      }
      this.attached = false;
      this.pipeline = null;
    }
  }

  /** Call from GameScene.shutdown() */
  shutdown(): void {
    if (this.attached && this.scene) {
      const cam = this.scene.cameras.main;
      if (cam) {
        cam.removePostPipeline('SlowmoFXPipeline');
      }
    }
    this.attached = false;
    this.pipeline = null;
    this.scene = null;
  }
}

export const SlowmoFXManager = new SlowmoFXManagerClass();
